import * as Sentry from '@sentry/react';
import { MapIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDevices, useDeviceStatusSocket, useIntl, useIsAdmin, useMe } from '../../hooks';
import { canManageSuppressions, canOperate } from '../../services/device-permissions.js';
import { Button, Input, Loading, Pagination } from '../ui';
import AddDeviceDialog from './AddDeviceDialog';
import DeviceBroadbandSuppressionsDialog from './DeviceBroadbandSuppressionsDialog';
import DeviceCard from './DeviceCard';
import DeviceDetectionsDialog from './DeviceDetectionsDialog';
import DeviceEventsDialog from './DeviceEventsDialog';
import DeviceTagFilter from './DeviceTagFilter';
import DeviceTelemetryDialog from './DeviceTelemetryDialog';

const PAGE_SIZES = [5, 10, 20] as const;
export const SERIAL_SEARCH_DEBOUNCE_MS = 300;

interface DeviceCardsProps {
  onSelectDevice?: (deviceId: string) => void;
  selectedDeviceId?: string | null;
  onShowAllDevices?: () => void;
}

export default function DevicesList({
  onSelectDevice,
  onShowAllDevices,
  selectedDeviceId,
}: DeviceCardsProps) {
  const intl = useIntl();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(PAGE_SIZES[2]);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [serialInput, setSerialInput] = useState('');
  const [serialQuery, setSerialQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = serialInput.trim();
      if (trimmed === serialQuery) return;
      setSerialQuery(trimmed);
      setPage(1);
    }, SERIAL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [serialInput, serialQuery]);

  const { data, isLoading, error, refetch } = useDevices({
    page,
    pageSize,
    // Sort-by-creation-date control was removed; the list keeps the
    // backend default order (createdAt DESC, newest first) so freshly-added
    // devices surface on the first page.
    onlineOnly,
    tagIds,
    serial: serialQuery,
  });
  const devices = useMemo(() => data?.devices ?? [], [data?.devices]);
  const total = data?.total ?? 0;
  // Stabilise the IDs array: only create a new reference when the actual IDs change,
  // not every time a device property (status, lastSeenAt, …) is updated in the cache.
  const allDeviceIdsKey = useMemo(() => devices.map((d) => d.id).join(','), [devices]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allDeviceIds = useMemo(() => devices.map((d) => d.id), [allDeviceIdsKey]);
  useDeviceStatusSocket({}, allDeviceIds);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const { data: me } = useMe();
  const isAdminUser = useIsAdmin(me);

  const missingEffectiveRoleReported = useRef(false);
  const hasDeviceWithoutEffectiveRole = devices.some(
    (device) => device.effectiveRole === undefined,
  );
  useEffect(() => {
    if (!me || isAdminUser || !hasDeviceWithoutEffectiveRole) return;
    if (missingEffectiveRoleReported.current) return;
    missingEffectiveRoleReported.current = true;
    Sentry.captureMessage('device payload without effectiveRole');
  }, [me, isAdminUser, hasDeviceWithoutEffectiveRole]);

  const handleSelectedTagIdsChange = (ids: string[]) => {
    setTagIds(ids);
    setPage(1);
  };

  const [settingsDeviceId, setSettingsDeviceId] = useState<string | null>(null);
  const [controlModalDeviceId, setControlModalDeviceId] = useState<string | null>(null);
  const [eventsModalDeviceId, setEventsModalDeviceId] = useState<string | null>(null);
  const [telemetryModalDeviceId, setTelemetryModalDeviceId] = useState<string | null>(null);
  const devicesWithLocation = devices.filter(
    (device) => device.settings?.latitude != null && device.settings?.longitude != null,
  );
  const controlModalDevice = controlModalDeviceId
    ? (devices.find((d) => d.id === controlModalDeviceId) ?? null)
    : null;
  const eventsModalDevice = eventsModalDeviceId
    ? (devices.find((d) => d.id === eventsModalDeviceId) ?? null)
    : null;
  const telemetryModalDevice = telemetryModalDeviceId
    ? (devices.find((d) => d.id === telemetryModalDeviceId) ?? null)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-lg font-semibold">
          {intl.formatMessage({ id: 'devicesList.title' })} {isLoading ? '' : `(${total})`}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={serialInput}
            onChange={(e) => setSerialInput(e.target.value)}
            aria-label={intl.formatMessage({ id: 'devicesList.serialSearchLabel' })}
            placeholder={intl.formatMessage({ id: 'devicesList.serialSearchPlaceholder' })}
            className="w-56"
            data-testid="devices-serial-search"
          />
          <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => {
                setOnlineOnly(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-border accent-primary"
              data-testid="devices-online-only"
            />
            {intl.formatMessage({ id: 'devicesList.onlineOnly' })}
          </label>

          {isAdminUser && <DeviceTagFilter onSelectedTagIdsChange={handleSelectedTagIdsChange} />}

          {!isLoading && (
            <>
              {isAdminUser && <AddDeviceDialog />}
              {onShowAllDevices && devicesWithLocation.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShowAllDevices}
                  className="flex items-center gap-2"
                  title={intl.formatMessage(
                    { id: 'devicesList.showDevicesOnMap' },
                    { count: devicesWithLocation.length },
                  )}
                >
                  <MapIcon className="h-4 w-4" />
                  {intl.formatMessage(
                    { id: 'devicesList.showAllOnMap' },
                    { count: devicesWithLocation.length },
                  )}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
                {intl.formatMessage({ id: 'devicesList.refresh' })}
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center">
          <Loading text={intl.formatMessage({ id: 'devicesList.loadingDevices' })} />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-destructive-dark">
          {intl.formatMessage({ id: 'devicesList.errorLoading' })}: {error.message}
        </div>
      ) : devices.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
          {devices.map((device) => {
            const canOperateDevice = canOperate(me, device);
            return (
              <DeviceCard
                key={device.id}
                device={device}
                isAdmin={isAdminUser}
                canAccessSettings={canOperateDevice}
                canAcknowledgeAlerts={canOperateDevice}
                showObserverView={!!me && !canOperateDevice}
                onSelectDevice={onSelectDevice}
                selectedDeviceId={selectedDeviceId}
                setControlModalDeviceId={setControlModalDeviceId}
                setEventsModalDeviceId={setEventsModalDeviceId}
                setSettingsDeviceId={setSettingsDeviceId}
                setTelemetryModalDeviceId={setTelemetryModalDeviceId}
                settingsDeviceId={settingsDeviceId}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center">
          {serialQuery
            ? intl.formatMessage(
                { id: 'devicesList.noDevicesMatchingSerial' },
                { query: serialQuery },
              )
            : intl.formatMessage({ id: 'devicesList.noDevicesFound' })}
        </div>
      )}

      {!isLoading && (
        <Pagination
          className="mt-2"
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size as (typeof PAGE_SIZES)[number]);
            setPage(1);
          }}
        />
      )}

      {controlModalDevice &&
        (controlModalDevice.capabilities?.includes('broadband_suppressions') ? (
          <DeviceBroadbandSuppressionsDialog
            deviceId={controlModalDevice.id}
            deviceSerialNumber={controlModalDevice.serial}
            deviceStatus={controlModalDevice.status}
            canOperate={canOperate(me, controlModalDevice)}
            onOpenChange={(open) => {
              if (!open) {
                setControlModalDeviceId(null);
              }
            }}
          />
        ) : (
          <DeviceDetectionsDialog
            deviceSerialNumber={controlModalDevice.serial}
            deviceId={controlModalDevice.id}
            canManageSuppressions={canManageSuppressions(me, controlModalDevice)}
            deviceCapabilities={controlModalDevice.capabilities}
            deviceStatus={controlModalDevice.status}
            isAdminUser={isAdminUser}
            onOpenChange={(open) => {
              if (!open) {
                setControlModalDeviceId(null);
              }
            }}
          />
        ))}

      {isAdminUser && eventsModalDevice && (
        <DeviceEventsDialog
          deviceId={eventsModalDevice.id}
          deviceSerialNumber={eventsModalDevice.serial}
          onOpenChange={(open) => {
            if (!open) {
              setEventsModalDeviceId(null);
            }
          }}
        />
      )}

      {telemetryModalDevice && (
        <DeviceTelemetryDialog
          deviceId={telemetryModalDevice.id}
          deviceSerialNumber={telemetryModalDevice.serial}
          onOpenChange={(open) => {
            if (!open) {
              setTelemetryModalDeviceId(null);
            }
          }}
        />
      )}
    </div>
  );
}
